import { useMemo } from 'react';
import classNames from 'classnames';
import { ButtonProps } from './button.types';
import { isDefined } from '@/utils/is-defined';

import styles from './button.module.css';
import SkeletonRect from '@ui/skeleton-loader/skeleton-rect';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';

const Button = (props: ButtonProps) => {
  const {
    loading,
    loadingColor,
    children,
    backgroundColor = 'inherit',
    hoverBackgroundColor,
    focusBackgroundColor,
    hoverOpacity,
    style,
    className,
    outlineOnFocus,
    height,
    border,
    hoverBorder,
    width,
    hoverChildrenColor,
    gradientProps,
    disabledOpacity,
    minWidth,
    maxWidth,
    hoverMinWidth,
    clear,
    position,
    borderRadius,
    opacity,
    'data-component': dataComponent = 'Button',
    padding,
    design,
    designSize,
    fixedState,
    outline,
    round,
    skLoader,
    onFocusAnimations,
    ...rest
  } = props;

  const stylesDefined = useMemo(
    () => ({
      border: isDefined(border),
      height: isDefined(height),
      width: isDefined(width),
      minWidth: isDefined(minWidth),
      maxWidth: isDefined(maxWidth),
      hoverBorder: isDefined(hoverBorder),
      hoverMinWidth: isDefined(hoverMinWidth),
      borderRadius: isDefined(borderRadius),
      opacity: isDefined(opacity),
      padding: isDefined(padding),
      fixedState: !!fixedState,
    }),
    [border, borderRadius, fixedState, height, hoverBorder, hoverMinWidth, maxWidth, minWidth, opacity, padding, width]
  );

  const hasLoader = !!skLoader;

  const globalDesignClasses = useMemo(
    () => ({
      /* these are not module classes, they are global classes */
      ...(design ? { [design]: design } : {}),
      ...(designSize ? { [`${designSize}-global-btn${round ? '-round' : ''}`]: designSize } : {}),
      ...(fixedState ? { [`${fixedState}-global-psuedo-state`]: fixedState } : {}),
      'can-have-loader': hasLoader,
    }),
    [design, designSize, fixedState, round, hasLoader]
  );

  return (
    <button
      data-component={dataComponent}
      data-design={design}
      className={classNames(
        styles.button,
        {
          ['clear-button']: clear,
          [styles['outline-on-focus']]: outlineOnFocus,
          'no-outline': !outlineOnFocus,
          [styles['hover-children-color']]: !!hoverChildrenColor,
          [styles['button-gradient']]: !!gradientProps,
          [styles['button-gradient-animate-on-hover']]: gradientProps?.animateOnHover ?? true,
          [styles['button-gradient-animate-on-focus']]: gradientProps?.animateOnFocus ?? true,
          [styles['button-disabled-opacity']]: disabledOpacity !== undefined,
          [styles['button-min-width']]: minWidth !== undefined,
          [styles['button-hover-min-width']]: hoverMinWidth !== undefined,
          [styles['button-gradient-lock-hover-state']]: gradientProps?.lockHoverState,
          [styles['button-radius']]: borderRadius !== undefined,
          [styles['button-padding']]: padding !== undefined,
          [styles['width']]: stylesDefined.width,
          [styles['minWidth']]: stylesDefined.minWidth,
          [styles['maxWidth']]: stylesDefined.maxWidth,
          [styles['height']]: stylesDefined.height,
          [styles['outline']]: !!outline,
          [styles['on-focus-animations']]: !!onFocusAnimations,
          ...globalDesignClasses,
        },
        className || ''
      )}
      style={{
        ...style,
        ...(backgroundColor ? { '--button-background-color': backgroundColor } : {}),
        ...(hoverBackgroundColor ? { '--button-hover-background-color': hoverBackgroundColor } : {}),
        ...(focusBackgroundColor ? { '--button-focus-background-color': focusBackgroundColor } : {}),
        ...(hoverChildrenColor ? { '--button-hover-children-color': hoverChildrenColor } : {}),
        ...{ '--button-hover-opacity': hoverOpacity },
        ...(stylesDefined.border ? { '--button-border': border } : {}),
        ...(stylesDefined.height ? { '--button-height': height } : {}),
        ...(stylesDefined.width ? { '--button-width': width } : {}),
        ...(stylesDefined.minWidth ? { '--button-min-width': minWidth } : {}),
        ...(stylesDefined.maxWidth ? { '--button-max-width': maxWidth } : {}),
        ...(stylesDefined.hoverBorder ? { '--button-hover-border': hoverBorder } : {}),
        ...(gradientProps?.color
          ? {
              '--button-gradient-color': gradientProps.color.startsWith('--')
                ? `var(${gradientProps.color})`
                : gradientProps.color,
            }
          : {}),
        ...(gradientProps?.hoverColor ? { '--button-gradient-hover-color': `var(${gradientProps.hoverColor})` } : {}),
        ...(disabledOpacity ? { '--button-disabled-opacity': disabledOpacity } : {}),
        ...(stylesDefined.hoverMinWidth ? { '--button-hover-min-width': hoverMinWidth } : {}),
        ...(position ? { '--button-position': position } : {}),
        ...(gradientProps?.startSize ? { '--button-gradient-start-size': gradientProps.startSize } : {}),
        ...(gradientProps?.startPosition ? { '--button-gradient-start-position': gradientProps.startPosition } : {}),
        ...(gradientProps?.endSize ? { '--button-gradient-end-size': gradientProps.endSize } : {}),
        ...(gradientProps?.endPosition ? { '--button-gradient-end-position': gradientProps.endPosition } : {}),
        ...(stylesDefined.borderRadius ? { '--button-border-radius': borderRadius } : {}),
        ...(stylesDefined.opacity ? { '--button-opacity': opacity } : {}),
        ...(stylesDefined.padding ? { '--button-padding': padding } : {}),
        ...(outline ? { '--button-outline': outline } : {}),
        ...(onFocusAnimations?.scale !== undefined ? { '--button-focus-scale': onFocusAnimations.scale } : {}),
        ...(onFocusAnimations?.opacity !== undefined ? { '--button-focus-opacity': onFocusAnimations.opacity } : {}),
        ...(onFocusAnimations?.svgChildrenFillColor
          ? { '--button-focus-svg-fill': onFocusAnimations.svgChildrenFillColor }
          : {}),
      }}
      {...rest}
    >
      <>
        {children}
        {loading ? (
          <div className={styles['loading-overlay']}>
            <WaitingIndicator fill={loadingColor} />
          </div>
        ) : null}
        {hasLoader ? <SkeletonRect {...skLoader} /> : null}
      </>
    </button>
  );
};

export default Button;
